<script lang="ts">
export default {
  name: "BaseBtn",
};
</script>

<script lang="ts" setup>
import { type RouterLinkProps } from "vue-router";
import {
  BtnTypesEnum,
  BtnVariantsEnum,
  BtnTonesEnum,
  BtnSizesEnum,
} from "@/shared/components/base/Btn/types";
import { BTN_CONTAINER } from "@/shared/components/base/Btn/context";
import { useAppNotifications } from "@/shared/composables/useAppNotifications";
import { useI18n } from "@/shared/composables/useI18n";

export type Props = {
  to?: RouterLinkProps["to"];
  href?: string;
  target?: HTMLAnchorElement["target"];
  type?: `${BtnTypesEnum}`;
  loading?: boolean;
  variant?: `${BtnVariantsEnum}`;
  tone?: `${BtnTonesEnum}`;
  /** Defaults to `sm`, or to the size set by a containing BtnGroup. */
  size?: `${BtnSizesEnum}`;
  block?: boolean;
  mobileIconOnly?: boolean;
  active?: boolean;
  disabled?: boolean;
  confirm?: boolean | string;
  /**
   * Class the router applies while the link is active. Defaults to the router's
   * global `active`, which Btn styles; pass "" to opt out - the paginator does,
   * so its arrows are not highlighted just because they point at the current page.
   */
  routeActiveClass?: string;
};

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  href: undefined,
  target: undefined,
  type: BtnTypesEnum.BUTTON,
  loading: false,
  variant: BtnVariantsEnum.SOLID,
  tone: BtnTonesEnum.NEUTRAL,
  size: undefined,
  block: false,
  mobileIconOnly: false,
  active: false,
  disabled: false,
  confirm: false,
  routeActiveClass: undefined,
});

const emit = defineEmits(["click"]);

const { t } = useI18n();

const container = inject(BTN_CONTAINER, null);

const isSegment = computed(
  () => container?.container === "group" && !!container.segmented?.value,
);

// Reported to the group so it can place its thumb; see BtnGroup. Registered for
// every group member, not just segmented ones, because `segmented` can flip at
// runtime and a member that never registered would leave a gap in the order.
if (container?.register) {
  const entry = container.register(
    () => props.active,
    () => props.tone,
  );
  onUnmounted(entry.unregister);
}

const size = computed(
  () => props.size ?? container?.size.value ?? BtnSizesEnum.SM,
);

const internalDisabled = ref(props.disabled);

watch(
  () => props.disabled,
  (value) => {
    internalDisabled.value = value;
  },
);

onMounted(() => {
  if (props.confirm && props.type === BtnTypesEnum.SUBMIT) {
    throw new Error(
      "BaseBtn: 'confirm' prop is not supported with type 'submit'",
    );
  }
});

// A disabled link is not disabled: `disabled` has no effect on <a>, so the old
// component left disabled href buttons fully clickable and focusable. Anything
// disabled renders as a real <button> instead.
const inert = computed(() => internalDisabled.value || props.loading);

const btnType = computed(() => {
  if (inert.value) return "button";

  if (props.to) return "router-link";

  if (props.href) return "a";

  return "button";
});

const btnProps = computed(() => {
  if (inert.value) {
    return { type: props.type, disabled: true };
  }

  if (props.to) {
    if (props.routeActiveClass === undefined) return { to: props.to };

    return {
      to: props.to,
      activeClass: props.routeActiveClass,
      exactActiveClass: props.routeActiveClass,
    };
  }

  if (props.href) {
    return {
      href: props.href,
      target: props.target ?? "_blank",
      rel: "noopener",
    };
  }

  return { type: props.type };
});

const cssClasses = computed(() => [
  `btn--${props.variant}`,
  `btn--${size.value}`,
  `btn--tone-${props.tone}`,
  {
    "btn--block": props.block,
    "btn--mobile-icon-only": props.mobileIconOnly,
    "btn--grouped": container?.container === "group",
    "btn--segment": isSegment.value,
    "btn--grouped-block":
      container?.container === "group" && container.block.value,
    "btn--menu-item": container?.container === "menu",
    active: props.active,
    "is-loading": props.loading,
  },
]);

const { displayConfirm } = useAppNotifications();

const handleClick = (event: MouseEvent) => {
  if (!props.confirm) {
    emit("click", event);
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  internalDisabled.value = true;

  displayConfirm({
    text: props.confirm === true ? undefined : props.confirm,
    onConfirm: () => {
      internalDisabled.value = false;
      if (props.href) {
        window.open(props.href, props.target ?? "_blank");
      } else {
        emit("click", event);
      }
    },
    onClose: () => {
      internalDisabled.value = false;
    },
  });
};
</script>

<template>
  <component
    :is="btnType"
    class="btn"
    :class="cssClasses"
    :aria-busy="loading || undefined"
    :role="isSegment ? 'radio' : undefined"
    :aria-checked="isSegment ? String(active) : undefined"
    v-bind="btnProps"
    @click="handleClick"
  >
    <span class="btn__content">
      <slot />
    </span>
    <!-- Announced for every loading button - the caps are visual only. The
         visible label is never replaced. -->
    <span v-if="loading" class="btn__status" role="status">
      {{ t("baseBtn.labels.loading") }}
    </span>
  </component>
</template>

<!--
  Plain CSS, not lang="scss", on purpose: @apply and @reference are silently
  dropped when sass preprocesses the block first (they reach the minifier as
  unknown at-rules). Compiling them here inlines each theme token with its
  literal fallback, which is what lets these styles work inside the embed
  bundle, where tailwind.css is never loaded.
-->
<style scoped>
@reference "../../../../entrypoints/tailwind.css";

.btn {
  @apply relative inline-flex items-center justify-center gap-2;
  /* 400, as the live button has always been. Semibold at 13-14px on a dark
     translucent surface thickens the strokes and closes the counters, so the
     label read as heavier and less legible than the one it replaced. */
  @apply font-normal whitespace-nowrap no-underline;
  @apply cursor-pointer border border-transparent bg-transparent;
  @apply transition-[background-color,border-color,color,outline-color] duration-150 ease-in-out;

  /* No margins. The old component shipped margin-right:10px/margin-bottom:20px,
     which is why 112 call sites passed `inline` purely to cancel it. Spacing is
     the parent's job - BtnGroup and page-actions use gap. */
  margin: 0;
}

.btn__content {
  @apply inline-flex items-center gap-2;
  /* min-w-0 lets the label shrink so text-ellipsis can engage inside the flex
     row. leading-tight rather than leading-none: with overflow hidden, a
     line-height of 1 clips glyph descenders - the "p" in "Grouped". */
  @apply min-w-0 overflow-hidden leading-tight text-ellipsis;
}

/*
 * The only remaining :deep rules, and both point at slot content this component
 * is laying out - never at another component's internals. Reaching across a
 * component boundary to restyle it is what produced the 118-line
 * `> :deep(.panel-btn)` override file this redesign deletes; if you need that,
 * add a prop or use BTN_CONTAINER instead.
 */
.btn :deep(img) {
  @apply block max-h-full w-4 max-w-full;
}

/* ---------- sizes ----------
   Heights match the form controls in FormInput (43px, and 55px for its large
   variant) so a button sits flush next to an input, and they preserve the
   historical button scale: old small 42px, default 48px, large 55px. */
/* min-width matches the height so an icon-only button stays square instead of
   collapsing to the width of its glyph - a fa-ellipsis-v is only a few px wide. */
/* Label sizes track the live button, which set 16px at every size. 13px was a
   three-point drop on the most-used size and is the other half of why the labels
   read worse than the ones they replaced; the heights are unchanged. */
/* xs is the chip scale, not a fourth step of the toolbar scale: 29px is exactly
   a chip's height (1px border + 6px padding + a 15px line box, twice over), so a
   control that sits in a chip row lines up with the chips beside it instead of
   towering 14px over them.

   7px of padding, not the 10px the height would suggest, because min-width only
   squares an icon-only button when the padding leaves room for it: sm is tuned so
   14 + 14 + 2 + a 13px glyph lands exactly on its 43px min-width, and xs has to
   hold the same identity against 29. At 10px the glyph pushes the box to 35px
   wide and min-width never applies. */
.btn--xs {
  @apply h-[29px] min-w-[29px] px-[7px] text-[15px];
}
/* At chip scale the control wears the chip's frame rather than the toolbar's -
   the softer edge and the smaller radius - so an add button reads as part of the
   row instead of a brighter, hollow box parked at the end of it. Two classes so
   the variant's own edge and radius do not win on order. */
.btn--xs.btn--solid,
.btn--xs.btn--ghost {
  @apply border-edge-soft rounded-control-bare;
}
.btn--sm {
  @apply h-[43px] min-w-[43px] px-3.5 text-[15px];
}
.btn--md {
  @apply h-12 min-w-12 px-4.5 text-base;
}
.btn--lg {
  @apply h-[55px] min-w-[55px] px-6 text-[17px];
}

/* ---------- end-caps ----------
   Always present. The inset is proportional so the cap holds a constant share
   of the width instead of collapsing on short buttons: the old fixed 14px each
   side left only 8px of cap on a 36px icon button. The 10px floor keeps it
   clear of the 8px corner radius.

   Geometry comes from the shared --cap-* tokens so a button cap and a panel cap
   are one motif rather than two that drifted. The button's height steps one
   below the panel's, and its radius steps down by the same ratio and is then
   held to half its own height - without that ceiling a 2px cap rounds far
   enough to read as a lozenge rather than a seam. Radius applies to the inward
   edge only, so the outward edge stays a line continuous with the border. */
.btn--solid::before,
.btn--solid::after,
.btn--ghost::before,
.btn--ghost::after {
  content: "";
  position: absolute;
  left: max(10px, var(--cap-inset, 12%));
  right: max(10px, var(--cap-inset, 12%));
  height: var(--cap-h-btn, 2px);
  /* Colour comes through a variable so the interaction states can stay on the
     element - a pseudo-element cannot be selected by :hover/:focus-visible
     without restating the whole selector four times over. The fallbacks are
     spelled out because a bare var() gets none from Tailwind and the embed
     bundle never registers :root. */
  background-color: var(--btn-cap, var(--color-endcap, #7a8288));
  transition: background-color 150ms ease-in-out;
}
.btn--solid::before,
.btn--ghost::before {
  top: -1px;
  border-radius: 0 0 var(--cap-r-btn, 1px) var(--cap-r-btn, 1px);
}
.btn--solid::after,
.btn--ghost::after {
  bottom: -1px;
  border-radius: var(--cap-r-btn, 1px) var(--cap-r-btn, 1px) 0 0;
}
/* No caps at chip scale, the call Chip and .panel--slim already make: the inset
   bottoms out at its 10px floor, so on a 29px icon button the cap would be a
   third of the width - the failure F3 of the label plan names. */
.btn--xs::before,
.btn--xs::after {
  content: none;
}
.btn--lg::before {
  height: var(--cap-h-btn-lg, 3px);
  border-radius: 0 0 var(--cap-r-btn-lg, 1.5px) var(--cap-r-btn-lg, 1.5px);
}
.btn--lg::after {
  height: var(--cap-h-btn-lg, 3px);
  border-radius: var(--cap-r-btn-lg, 1.5px) var(--cap-r-btn-lg, 1.5px) 0 0;
}

/* ---------- variants ---------- */
.btn--solid {
  @apply bg-control border-edge text-text rounded-control;
}
.btn--ghost {
  @apply border-edge text-text rounded-control bg-transparent;
}
.btn--bare {
  @apply text-text rounded-control-bare border-transparent bg-transparent;
}

/*
 * Hover, press and focus light the end-cap and leave the frame alone. Recolouring
 * the border swapped the entire outline of the control, which in a toolbar reads
 * as the button changing shape rather than responding; the cap is already this
 * component's signature, so it is the piece that answers. Same call the panel
 * made for tone - see docs/exec-plans/panel-redesign.md.
 */
.btn--solid:hover:not([disabled]),
.btn--solid.active {
  @apply bg-control-hover text-lifted;
  --btn-cap: var(--color-primary, #428bca);
}
.btn--ghost:hover:not([disabled]),
.btn--ghost.active {
  @apply bg-control-hover text-lifted;
  --btn-cap: var(--color-primary, #428bca);
}
.btn--bare:hover:not([disabled]),
.btn--bare.active {
  @apply text-lifted;
  background-color: rgb(122 130 136 / 0.12);
}

.btn--solid:active:not([disabled]),
.btn--ghost:active:not([disabled]) {
  @apply bg-control-press;
  /* Dimmer than hover, so the press reads as the control receding along with
     its surface rather than as a second, brighter hover. */
  --btn-cap: rgb(66 139 202 / 0.6);
}

/* The outline stays - see the focus rule below. This only brings the cap along,
   so a keyboard-focused button and a hovered one are lit the same way. */
.btn--solid:focus-visible,
.btn--ghost:focus-visible {
  --btn-cap: var(--color-primary, #428bca);
}

/* ---------- tone ----------
 * The cap carries the tone, and only the cap. The frame, the surface and the
 * label all stay exactly as a neutral button's, so a destructive control reads
 * as an ordinary one wearing a red signature. The earlier #f0a8ae label tint is
 * gone: pink text on the neutral surface read as a disabled or error state
 * rather than as an available action.
 *
 * Consequence worth knowing: bare, grouped and menu-item set `content: none` on
 * their caps, so those variants carry no resting marker at all and rely on their
 * hover tint below.
 */
.btn--tone-danger {
  --btn-cap: var(--color-danger, #dc3545);
}
/*
 * A danger button floods on hover, so it cannot borrow the primary cap the
 * neutral states use - blue on that red surface. Its cap follows the label to
 * white instead, which keeps the signature legible through the flood. The
 * tone-plus-variant pair outranks the neutral hover/press/focus rules.
 */
.btn--tone-danger.btn--solid:hover:not([disabled]),
.btn--tone-danger.btn--ghost:hover:not([disabled]) {
  @apply bg-danger border-danger text-white;
  --btn-cap: rgb(255 255 255 / 0.65);
}
.btn--tone-danger.btn--solid:focus-visible,
.btn--tone-danger.btn--ghost:focus-visible {
  --btn-cap: rgb(255 255 255 / 0.65);
}
.btn--tone-danger.btn--bare:hover:not([disabled]) {
  @apply text-white;
  background-color: rgb(220 53 69 / 0.18);
}

/*
 * Warning says "this is not the usual choice" rather than "this destroys
 * something", so it follows danger's shape exactly and only changes the colour.
 * Same consequence as danger: grouped and bare members drop their cap, so a
 * warning member of a BtnGroup is marked by the group's thumb rather than here,
 * and both tones need the grouped and menu hover tints further down - without
 * them the flood below applies inside a container that cannot take it.
 */
.btn--tone-warning {
  --btn-cap: var(--color-warning, #fa6800);
}
.btn--tone-warning.btn--solid:hover:not([disabled]),
.btn--tone-warning.btn--ghost:hover:not([disabled]) {
  @apply bg-warning border-warning text-white;
  --btn-cap: rgb(255 255 255 / 0.65);
}
.btn--tone-warning.btn--solid:focus-visible,
.btn--tone-warning.btn--ghost:focus-visible {
  --btn-cap: rgb(255 255 255 / 0.65);
}
.btn--tone-warning.btn--bare:hover:not([disabled]) {
  @apply text-white;
  background-color: rgb(250 104 0 / 0.18);
}

/* ---------- states ---------- */
.btn[disabled] {
  @apply cursor-default;
}

/*
 * Dim the content, not the whole element. Element-wide opacity makes an opaque
 * surface translucent, so a disabled member of a BtnGroup let the lighter track
 * show through and read *lighter* than its enabled siblings - the pagination
 * arrows looked highlighted rather than dimmed.
 */
.btn[disabled] .btn__content {
  @apply opacity-45;
}

.btn--solid[disabled],
.btn--ghost[disabled] {
  @apply border-edge-soft;
}

.btn--solid[disabled]::before,
.btn--solid[disabled]::after,
.btn--ghost[disabled]::before,
.btn--ghost[disabled]::after {
  @apply opacity-50;
}

/* The old component set outline:none with no replacement, so keyboard focus was
   invisible. outline follows border-radius and is never clipped, unlike the
   box-shadow ring a clip-path or overflow:hidden parent would crop. */
.btn:focus-visible {
  @apply outline-primary outline-2 outline-offset-2;
}

.btn--block {
  @apply w-full;
}

/* ---------- inside a BtnGroup ----------
   The group draws one border, one radius and one pair of end-caps for the whole
   control, so members carry no chrome at all. Applied here rather than from
   BtnGroup's stylesheet, so Btn owns its own appearance in every context. */
/*
 * Same surface as a standalone button, so a group does not read as a different
 * material - and the member draws the hairline that divides it from the member
 * before it, rather than the track showing through a gap behind it. The fill is
 * translucent, so a track painting the seam sat *behind* every member and tinted
 * it: a member's rest read four points lighter than the same token standalone,
 * and the leading edge of that 1px shadow is clipped by the track's own
 * overflow, so no member needs to know it is the first.
 */
.btn--grouped {
  @apply bg-control rounded-none border-0;
  box-shadow: -1px 0 0 var(--color-seam, #3a3f44);
}
.btn--grouped::before,
.btn--grouped::after {
  content: none;
}
/* The standalone hover fill, not a value of its own. The literal #2b3034 this
   replaces lifted the member three points off its rest where a standalone button
   lifts fifteen, so a group answered the pointer about a quarter as loudly. */
.btn--grouped:hover:not([disabled]) {
  @apply bg-control-hover text-lifted;
}
/* Same reasoning as the menu item: a group member is a flat fill inside one
   shared surface, so flooding it edge to edge breaks the control it sits in. */
.btn--grouped.btn--tone-danger:hover:not([disabled]) {
  @apply text-white;
  background-color: rgb(220 53 69 / 0.35);
}
/* Warning needs its own, or the standalone rule above floods the segment solid
   orange to its square edges - which is the exact failure the danger rule
   exists to avoid. */
.btn--grouped.btn--tone-warning:hover:not([disabled]) {
  @apply text-white;
  background-color: rgb(250 104 0 / 0.35);
}
/*
 * Engaged, not hovered. A grouped member has no end-caps to carry state, and a
 * 0.22 tint sat too close to the neutral hover fill to be told apart - so this
 * adds a second signal the hover state cannot borrow: an inset rule along the
 * bottom edge, which reads as a pressed key rather than a lit one. The divider
 * comes along, since this shadow replaces rather than extends the base one.
 */
.btn--grouped.active,
.btn--grouped[aria-pressed="true"] {
  @apply text-white;
  background-color: rgb(66 139 202 / 0.26);
  box-shadow:
    inset 0 -2px 0 var(--color-primary, #428bca),
    -1px 0 0 var(--color-seam, #3a3f44);
}

/* Hovering something already on has to answer too: `.active` outranks the
   neutral grouped hover, so without this an engaged toggle felt dead. */
.btn--grouped.active:hover:not([disabled]),
.btn--grouped[aria-pressed="true"]:hover:not([disabled]) {
  background-color: rgb(66 139 202 / 0.38);
}
.btn--grouped:focus-visible {
  @apply outline-offset-[-2px];
}

/* Set by BtnGroup via context rather than by a `> :deep(*)` rule from the
   group's stylesheet, so the member still owns its own layout. */
.btn--grouped-block {
  @apply flex-1;
}
/* No :first-child/:last-child radii here on purpose: BtnGroup's inner track
   clips the end corners, so a member never needs to know its position - which
   also survives a member being wrapped by another component. */

/* ---------- inside a segmented BtnGroup ----------
 * The thumb is the fill, so a segment paints nothing of its own - including on
 * hover and when active, which is what stops "selected" and "hovered" being the
 * same treatment. State lives in the type instead.
 */
.btn--segment,
.btn--segment:hover:not([disabled]),
.btn--segment.active,
/* The grouped active-hover fill is four selectors deep, so it outranks a bare
   `.btn--segment.active` and floods the chosen segment on hover. */
.btn--segment.active:hover:not([disabled]) {
  @apply bg-transparent;
  /* The grouped active state's accent rule as well as its fill: the thumb already
     says which segment is chosen, and an underline under it says it twice. */
  box-shadow: none;
}

.btn--segment:hover:not([disabled]),
.btn--segment.active {
  @apply text-lifted;
}

/* A tone must not bring a fill back. The grouped tints below are written for a
   plain group, where a member is its own surface; in a switch the thumb is the
   only fill there is, and a toned segment that floods on hover reads as a
   second, differently shaped thumb. Four selectors deep to match those rules,
   and after them so it wins on order. The tone still shows: it colours the
   thumb once the segment is chosen. */
.btn--segment.btn--tone-danger:hover:not([disabled]),
.btn--segment.btn--tone-warning:hover:not([disabled]) {
  @apply bg-transparent;
}

/* ---------- inside a BtnDropdown list ---------- */
.btn--menu-item {
  @apply w-full justify-start rounded-none border-0 bg-transparent;
}
.btn--menu-item::before,
.btn--menu-item::after {
  content: none;
}
.btn--menu-item:hover:not([disabled]) {
  @apply text-lifted;
  background-color: rgb(122 130 136 / 0.16);
}
/*
 * A menu item has no surface of its own, so the solid variant's danger hover -
 * which floods bg-danger edge to edge - turns a dropdown row into a solid red
 * block. `bare` already carries the tint treatment for exactly this reason; the
 * menu context needs it too, since `variant` defaults to solid and a dropdown
 * item is almost never given one explicitly.
 *
 * Equal specificity to the solid rule, so this has to stay below it.
 */
.btn--menu-item.btn--tone-danger:hover:not([disabled]) {
  @apply text-white;
  background-color: rgb(220 53 69 / 0.18);
}
.btn--menu-item.btn--tone-warning:hover:not([disabled]) {
  @apply text-white;
  background-color: rgb(250 104 0 / 0.18);
}

/* ---------- loading ---------- */
/* The label is kept. The old component replaced it with "Loading", which
   changed the accessible name mid-interaction. */
.btn__status {
  @apply sr-only;
}

/*
 * The two caps carry the state, as one run travelling around the outside: it
 * fills the top cap left to right, carries on along the bottom cap right to
 * left, and the tail follows the same way round. One direction, so the eye
 * follows it rather than watching two things meet. Nothing is inserted next to
 * the label, so the button neither grows nor reflows on the frame it starts
 * working.
 *
 * This is the state the component had no answer for. It used to depend on an
 * opt-in `spinner`, which was passed at 12 of 51 loading call sites - the other
 * 39 showed nothing at all and only read as a button that had gone dead. The
 * caps answer for every one of them, so that prop is gone: the caps are already
 * this component's signature, the same call the hover and tone rules above make,
 * and a rhombus dropped into the content row cost a reflow besides.
 *
 * A marker travelling along the cap was the first shape of this and it does not
 * survive the narrow case: an icon-only sm button leaves a 23px cap, where the
 * segment read as two nubs parked in the corners rather than as motion. A fill
 * is always one run anchored to an edge, so it still reads at that length.
 *
 * Two legs of 0.9s, and neither cap ever waits: the moment the head moves onto
 * the bottom cap, the top starts clearing behind it, so the lit run is one cap
 * long and always moving. That also means each cap only ever travels one way -
 * the top rightwards whether it is filling or clearing, the bottom leftwards -
 * which is what lets two bars read as one thing going round. The anchor swaps
 * once per leg, always while the cap is full or empty, where it cannot be seen.
 */
.btn.is-loading::before,
.btn.is-loading::after {
  /* The empty cap, dimmer than at rest, so a cap the run has left is visibly the
     same cap rather than one that went missing. */
  background-color: rgb(122 130 136 / 0.35);
  background-repeat: no-repeat;
  background-size: 0% 100%;
  /* Linear, not eased: an ease per leg puts a hesitation at each corner, and
     this is one run going round at one speed. */
  animation-duration: 1.8s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  /* A loading button renders disabled, and the disabled rule halves the caps.
     The part carrying the state has to stay at full strength - this has to sit
     below that rule, which it ties with on specificity. */
  opacity: 1;
}

/* The leading edge fades rather than ending on a hard line: a solid edge reads
   as a progress bar reporting a percentage, and there is nothing to report. */
.btn.is-loading::before {
  background-image: linear-gradient(
    to right,
    var(--btn-cap, var(--color-endcap, #7a8288)) 80%,
    transparent 100%
  );
  background-position: left center;
  animation-name: btn-fill-rightward;
}

.btn.is-loading::after {
  background-image: linear-gradient(
    to left,
    var(--btn-cap, var(--color-endcap, #7a8288)) 80%,
    transparent 100%
  );
  background-position: right center;
  animation-name: btn-fill-leftward;
}

/* In from the left, then out to the right: the fill grows from the left edge,
   and once it covers the whole run the anchor moves to the right edge so the
   second half clears it in the same direction rather than retreating. The top
   cap and every cap-less surface run on this one. */
@keyframes btn-fill-rightward {
  0% {
    background-position: left center;
    background-size: 0% 100%;
  }
  50% {
    background-position: left center;
    background-size: 100% 100%;
  }
  50.01% {
    background-position: right center;
    background-size: 100% 100%;
  }
  100% {
    background-position: right center;
    background-size: 0% 100%;
  }
}

/* The same run mirrored, and half a cycle into it: the bottom cap clears
   leftwards while the top fills, and fills from the right while the top clears.
   That is the hand-over at each corner. */
@keyframes btn-fill-leftward {
  0% {
    background-position: left center;
    background-size: 100% 100%;
  }
  50% {
    background-position: left center;
    background-size: 0% 100%;
  }
  50.01% {
    background-position: right center;
    background-size: 0% 100%;
  }
  100% {
    background-position: right center;
    background-size: 100% 100%;
  }
}

/* Neutral has no colour of its own to fill with - its resting cap is the same
   grey as the empty state - so it borrows the hover accent. Two classes, so
   danger and warning keep filling in their own tone. */
.btn--tone-neutral.is-loading {
  --btn-cap: var(--color-primary, #428bca);
}

/*
 * Busy is not unavailable. The disabled rule dims the content to 45%, which for
 * a working button says the wrong thing - it reads as a control you cannot use
 * rather than one that is already doing what you asked. The caps say it instead.
 */
.btn.is-loading .btn__content {
  @apply opacity-100;
  /* Above the wash below. Both are positioned boxes at z-index auto, and the
     pseudo-element comes after the label in tree order, so without this the
     wash paints over the text and tints it. */
  position: relative;
  z-index: 1;
}

/*
 * The contexts that drop their caps - chip scale, bare, and the two container
 * ones - have nothing to fill, and the edges are not theirs to use: a rail along
 * a group member's bottom lands directly under the group's own cap and reads as
 * a dirty edge, clipped by the track's rounding at that. So the surface fills
 * instead, in the tone colour and on the top cap's own keyframes: head in from
 * the left, tail out to the right, always that way. There is no circuit to run
 * on a surface, so it takes the one leg rather than doubling back. The fill is
 * the language; only the thing being filled changes with the context.
 */
.btn.is-loading.btn--xs::after,
.btn.is-loading.btn--bare::after,
.btn.is-loading.btn--grouped::after,
.btn.is-loading.btn--menu-item::after {
  content: "";
  position: absolute;
  inset: 0;
  /* The cap rules above set a 2px height on a solid xs; this box is the whole
     control, so it takes its height from the inset. */
  height: auto;
  border-radius: inherit;
  background-color: transparent;
  background-image: linear-gradient(
    var(--btn-cap, var(--color-primary, #428bca)),
    var(--btn-cap, var(--color-primary, #428bca))
  );
  background-repeat: no-repeat;
  background-size: 0% 100%;
  /* The strength of a hover tint, and well under the grouped active tint of
     0.26, so a working member never reads as louder than a chosen one. At 0.2 a
     bare button - the quietest variant there is - became a solid block of
     tone. */
  opacity: 0.16;
  z-index: 0;
  /* The top cap's run, but at half its pace. A surface is the whole control
     rather than a 2px edge, so the same speed reads as the button flashing;
     slower, it reads as the same idea kept quiet. */
  animation: btn-fill-rightward 3.6s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .btn,
  .btn--solid::before,
  .btn--solid::after,
  .btn--ghost::before,
  .btn--ghost::after {
    transition-duration: 1ms;
  }

  /* Nothing fills. Both caps hold steady at the tone colour, and the wash holds
     at its lit end, so the state is still marked where motion was the only
     thing carrying it. */
  .btn.is-loading::before,
  .btn.is-loading::after {
    animation-name: none;
    background-image: none;
    background-color: var(--btn-cap, var(--color-endcap, #7a8288));
  }

  .btn.is-loading.btn--xs::after,
  .btn.is-loading.btn--bare::after,
  .btn.is-loading.btn--grouped::after,
  .btn.is-loading.btn--menu-item::after {
    animation-name: none;
    /* Filled, not empty: without the animation the base size is 0 and the
       surface would carry no marker at all. */
    background-size: 100% 100%;
  }
}

@media (max-width: 992px) {
  /* Visually icon-only, but the label stays in the accessibility tree so the
     button keeps its accessible name. font-size:0 rather than hiding an element,
     because slot labels are usually bare text nodes, not wrapped in a span. */
  .btn--mobile-icon-only {
    @apply px-2.5;
  }

  .btn--mobile-icon-only .btn__content {
    @apply gap-0 text-[0px];
  }

  .btn--mobile-icon-only .btn__content :deep(i),
  .btn--mobile-icon-only .btn__content :deep(svg),
  .btn--mobile-icon-only .btn__content :deep(img) {
    @apply text-base;
  }
}
</style>
